import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';

const TOKEN_KEY = 'nh_auth_token';

/**
 * useFlowSocket — React hook for real-time patient flow updates.
 * Connects to the /flow Socket.IO namespace and returns live snapshot data.
 */
export function useFlowSocket(practiceId) {
  const [snapshot, setSnapshot] = useState(null);
  const [lastEvent, setLastEvent] = useState(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);

  const connect = useCallback(() => {
    if (!practiceId) return;

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    const serverUrl = window.location.origin.replace(':3000', ':3001');
    const socket = io(`${serverUrl}/flow`, {
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 15000,
    });

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('joinPracticeFlow', { practiceId });
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('flowSnapshot', (data) => {
      setSnapshot(data);
    });

    socket.on('flowEvent', (data) => {
      setLastEvent(data);
    });

    socket.on('connect_error', (error) => {
      console.error('[FlowSocket] Connection error:', error.message);
    });

    socketRef.current = socket;
  }, [practiceId]);

  useEffect(() => {
    connect();
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [connect]);

  return { snapshot, lastEvent, connected };
}
